import {mapGetters, mapState} from 'vuex';
import Vue from 'vue';
import {solutionMixin} from '@/utils/solution-mixin';
import CONFIG from '@/constants/config.js';
import {EspotoQREvaluator} from '@/utils/espoto-qr-evaluator.mjs';

export var spotMixin = {
    mixins: [solutionMixin],
    watch: {
        '$route.params': {
            handler: function(params) {
                if (params) {
                    this.urlSpotId = params.id;
                }
            },
            deep: true,
            immediate: true,
        },
    },

    data() {
        return {
            urlSpotId: null,
        };
    },
    computed: {
        ...mapGetters('spots', [
            'allSpots',
            'allActiveSpots',
            'allActiveVisibleSpots',
            'allAutoCheckinSpots',
            'autoCheckinSuppressedIds',
            'hasActiveSpots',
            'getOfflineStatistic',
            'hasQrCheckin',
            'hasPwCheckin'
        ]),
        ...mapState('spots', [
            'list',
            'groups_routing',
            'group_actions',
            'action_timers',
            'list_loading',
            'spot_loading',
            'spot_solve_loading',
            'hint_request_loading',
            'sort_order',
            'list_error',
        ]),

        isLoadingSpots() {
            return this.list_loading;
        },
        isLoadingSpot() {
            return this.spot_loading;
        },
        isLoadingHintRequest() {
            return this.hint_request_loading;
        },
        hasSpots() {
            return !Vue._.isEmpty(this.allSpots);
        },
        hasAvailableSpots() {
            // spots hidden by hide_on_solve are played content, not missing content -
            // otherwise an event made only of such spots would never count as complete
            return (
                !Vue._.isEmpty(this.allActiveSpots) ||
                Vue._.some(this.allSpots, (s) => s && s.hide_on_solve && this.spotAlreadyTouched(s))
            );
        },
        completeEvent() {
            let res = false;
            if (this.hasAvailableSpots) {
                let unsolvedSpot = Vue._.find(this.allActiveVisibleSpots, (item) => {
                    return !this.spotAlreadyTouched(item);
                });
                res = this._.isEmpty(unsolvedSpot);
            }
            // console.log(`CompleteEvent: ${res}`);
            return res;
        },
        unsolvedAutoCheckinSpots() {
            const suppressed = this.autoCheckinSuppressedIds || [];
            return Vue._.filter(this.allAutoCheckinSpots, (s) => {
                return !this.spotAlreadyTouched(s) //Just a security function. TODO remove that
                    && !suppressed.includes(s.id);
            });
        },
    },
    methods: {
        getSpotByPassword(password) {
            let res = [];
            if (this.hasSpots && Vue._.isString(password) && !Vue._.isEmpty(password.trim())) {
                res = Vue._.find(this.allActiveSpots, (item) => {
                    return item && item.password && password.toLowerCase() == item.password.toLowerCase();
                });
            }
            return res;
        },
        /**
         * Get spot by QR code using enhanced evaluation
         * @param {string} qrCode - QR code string
         * @returns {Object|false} - Spot object or false if not found
         */
        getSpotByQrCode(qrCode) {
            if (!qrCode || typeof qrCode !== 'string') {
                return false;
            }

            try {
                // Pass this context to the QR evaluator so it can use getSpotByPassword
                const qrEvaluator = new EspotoQREvaluator(qrCode, this);

                // Check if it's a valid task QR
                if (qrEvaluator.isTaskQR()) {
                    const taskId = qrEvaluator.readTaskIdFromQRCode();
                    if (taskId > 0) {
                        // Find spot by task ID
                        return Vue._.find(this.allActiveSpots, (spot) => {
                            return spot && spot.id === taskId;
                        });
                    }
                }

                // Fallback to original logic for custom QR codes
                return false;
            } catch (error) {
                console.error('Error parsing QR code:', error);
                return false;
            }
        },

        setSpotLoading(isLoading) {
            this.$store.commit('spots/SET_SPOTS_LOADING', isLoading);
        },
        addSpotObject(item) {
            this.$store.commit('spots/ADD_SPOT', item);
        },
        setSortOrder(order) {
            this.$store.commit('spots/SET_SORT_ORDER', order);
        },
        getSpotsByEventId(eventId) {
            return this.$store.dispatch('spots/getSpotsByEventId', eventId);
        },
        getSpotsByEventIdSilent(eventId) {
            return this.$store.dispatch('spots/getSpotsByEventId', { eventId: eventId, silent: true });
        },
        getSpotById(id) {
            return this.$store.dispatch('spots/getSpotById', id);
        },
        leaveSpot(id) {
            return this.$store.dispatch('spots/leaveSpot', id);
        },
        checkinSpot(id, eventId, timezone, doNotSend = false, onError = null, onResponse = null) {
            return this.$store.dispatch('spots/checkinSpot', {
                id: id,
                eventId: eventId,
                timezone: timezone,
                onError: onError,
                onResponse: onResponse,
                doNotSend: doNotSend,
            });
        },
        resetCheckinSpot(id) {
            return this.$store.dispatch('spots/resetCheckinSpot', id);
        },
        suppressAutoCheckin(id) {
            return this.$store.dispatch('spots/suppressAutoCheckin', id);
        },
        solveSpot(id, eventId, timezone, serverData, onError = null, onSuccess = null) {
            return this.$store.dispatch('spots/solveSpot', {
                id: id,
                eventId: eventId,
                timezone: timezone,
                serverData: serverData,
                onError: onError,
                onSuccess: onSuccess,
            });
        },
        requestHint(hintId, wpId, eventId, timezone, serverData, onError = null, onSuccess = null) {
            return this.$store.dispatch('spots/requestHint', {
                hintId: hintId,
                wpId: wpId,
                eventId: eventId,
                timezone: timezone,
                serverData: serverData,
                onError: onError,
                onSuccess: onSuccess,
            });
        },

        getSpotSibling(ar, id, siblingsCount, attr = 'id') {
            let index = Vue._.findIndex(ar, [attr, id]);
            return this.getAtIndex(ar, siblingsCount, index);
        },
        getAtIndex(ar, i, currentIndex = 0) {
            if (!Vue._.isArray(ar)) {
                return null;
            }
            if (i === 0) {
                return ar[currentIndex];
            } else if (i < 0) {
                return ar[(currentIndex + ar.length + i) % ar.length];
            } else if (i > 0) {
                return ar[(currentIndex + i) % ar.length];
            }
        },
        taskArrowColor(item, eventSettingsGpsDisabled, opacity = 1) {
            return item && this.spotIsActive(item) ?
                (
                    ( this.spotMustBeInRange(item, eventSettingsGpsDisabled) && !this.spotIsInRange(item) )?
                        CONFIG.getRed(opacity)
                        : CONFIG.getGreen(opacity)
                )
                : 'grey';
        },
        taskOverlayColor(item, opacity = 0.1) {
            return item
                ? this.spotIsInactive(item)
                    ? CONFIG.getBlack(opacity)
                    : this.spotIsSolved(item)
                        ? CONFIG.getGreen(opacity)
                        : this.spotIsFailed(item)
                            ? CONFIG.getRed(opacity)
                            : null
                : null;
        },
        taskStatusIcon(item) {
            return item
                ? this.spotIsInactive(item)
                    ? null
                    : this.spotIsSolved(item)
                        ? 'mdi-check-decagram'
                        : this.spotIsFailed(item)
                            ? 'mdi-close'
                            : null
                : null;
        },
        isAutoCheckinSpot(item){
            let autoCheckinSpot = this.allAutoCheckinSpots.find(obj => {
                return obj.id === item.id;
            });
            return autoCheckinSpot?true:false;
        }
    },
};
